import { AspectRatio, Image, Progress } from '@mantine/core'
import { Typography } from '../common/typography'
import { usePlayerDetails } from '../players/api'
import { useMetrics, DataPoint, useMetricsData } from '../widgets/api'
import { FmtColored, useFmtColor } from '../widgets/format'
import { useMemo } from 'react'
import classes from './Matches.module.css'

export default function MvpCard({
  player_id,
  competition_id,
  isHomeMvp,
  round_start,
}: {
  player_id: number
  competition_id: number
  isHomeMvp: boolean
  round_start: string
}) {
  const { data: player } = usePlayerDetails({
    competition_id: competition_id,
    player_id: player_id,
  })

  const { data: metrics } = useMetrics()
  const kpis = metrics.filter((m) => m.is_kpi && m.is_used_in_matches)
  const metricKeys = kpis.map((m) => m.key)

  const { data: kpiData } = useMetricsData({
    period_type: 'round',
    period_range: false,
    periods: [round_start],
    subject_type: 'user',
    subject_ids: [player.user_id || 0],
    metrics: metricKeys,
    components: metricKeys.map(() => 'percent'),
    statistics: metricKeys.map(() => 'value'),
    competition_id: competition_id,
  })

  const metricValues = useMemo(() => {
    const map = new Map<number, number>()
    kpiData?.forEach((point: DataPoint) => {
      if (point.metric && point.value !== null) {
        map.set(point.metric, point.value)
      }
    })
    return map
  }, [kpiData])

  const isPlaceholderPhoto = /\.svg$/i.test(player.photo.thumbnail)

  return (
    <div
      className={classes.mvpComponentContainer}
      style={
        {
          '--border-color': isHomeMvp ? '#8073ad' : '#B2675E',
        } as React.CSSProperties
      }
    >
      <div className={classes.mvpInfo}>
        <AspectRatio ratio={1 / 1}>
          <Image
            src={player.photo.thumbnail}
            alt={player.name}
            className={classes.mvpPhoto}
            fit={isPlaceholderPhoto ? 'contain' : 'scale-down'}
          />
        </AspectRatio>
        <Typography fs="sm" lh="150">
          {player.name}
        </Typography>
      </div>

      <div className={classes.mvpKpis}>
        {kpis.map((kpi) => {
          const value = metricValues.get(kpi.metric_id)

          return (
            <KpiRow
              key={kpi.key}
              title={kpi.short_name || kpi.name}
              value={value}
              metric={kpi.metric_id}
            />
          )
        })}
      </div>
    </div>
  )
}

function KpiRow({
  title,
  value,
  metric,
}: {
  title: string
  value: number | null | undefined
  metric: string | number
}) {
  const color = useFmtColor({
    metric,
    value,
    component: 'percent',
    statistic: 'value',
  })

  return (
    <div className={classes.mvpKpiItem}>
      <div className={classes.mvpKpiRow}>
        <Typography fs="xs" fc={{ light: 'black', dark: 'gray-3' }} lh="150">
          {title}
        </Typography>
        <Typography fs="xs" ff="JetBrains Mono" lh="140">
          <FmtColored
            value={value}
            metric={metric}
            component="percent"
            statistic="value"
            override={{ percent_decimal_places: 1 }}
          />
        </Typography>
      </div>
      <Progress color={color} value={(value || 0) * 100} />
    </div>
  )
}
